import { AppError } from '@repo/error';
import { errorMessage, number, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import {
  AssessedRiskLevelSchema,
  isReportCompleted,
  type Report,
  ReportDecision,
  ReportListStatus,
  ReportListStatusSchema,
  ReportSortBy,
  ReportSortOrder,
  ReportStatus,
  ReportType,
  RiskLevel,
} from '@repo/shared/schema';
import { UserRole } from '@repo/shared/types';
import {
  CheckCircleIcon,
  FileTextIcon,
  LayersIcon,
  LoaderCircleIcon,
  RotateCcwIcon,
  UserRoundIcon,
  UsersIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { isDev } from '@/lib/settings';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';
import { ListToolbar } from './list-toolbar';
import { dateLabel, OLink, Pagination, QueryState, RiskBadge } from './shared';

export const ReportRows = ({ reports }: { reports: Report[] }) => {
  useLocale();
  return (
    <div className="raven-table-frame overflow-auto">
      <table className="raven-table">
        <thead>
          <tr>
            <th>{t('reports.clientName')}</th>
            <th>{t('workspace.lastReview')}</th>
            <th>{t('reports.clientType')}</th>
            <th>{t('reports.status')}</th>
            <th>{t('reports.riskLevel')}</th>
            <th>{t('reports.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const isReady = isReportCompleted(report.status);
            const isDraft = report.status === ReportStatus.DRAFT;
            const canOpen = isReady || isDraft || isDev;
            const outcome = report.decision?.outcome;
            return (
              <tr key={report._id}>
                <td>
                  <div className="flex items-center gap-3">
                    <span aria-hidden="true" className="raven-avatar" />
                    <span className="raven-client-name">
                      {report.reportName}
                      <small className="raven-muted mt-1 block font-sans">
                        {dateLabel(report.createdAt)}
                      </small>
                    </span>
                  </div>
                </td>
                <td>{isReady ? dateLabel(report.generatedAt ?? report.updatedAt, true) : '—'}</td>
                <td>
                  <span className="raven-risk">
                    {report.reportType === ReportType.KYB ? (
                      <>
                        <UsersIcon className="me-1 inline size-3" />
                        {t('reports.business')}{' '}
                      </>
                    ) : (
                      <>
                        <UserRoundIcon className="me-1 inline size-3" />
                        {t('reports.individual')}{' '}
                      </>
                    )}
                  </span>
                </td>
                <td>
                  <span
                    className="raven-risk"
                    data-risk={
                      outcome === ReportDecision.ACCEPTED
                        ? RiskLevel.LOW
                        : outcome === ReportDecision.REJECTED
                          ? RiskLevel.HIGH
                          : RiskLevel.MEDIUM
                    }
                  >
                    {!isReady
                      ? isDraft
                        ? t('reports.savedDraft')
                        : t('reports.pending')
                      : outcome === ReportDecision.ACCEPTED
                        ? t('reports.clientAdded')
                        : outcome === ReportDecision.REJECTED
                          ? t('reports.clientRejected')
                          : t('reports.pendingReview')}
                  </span>
                </td>
                <td>
                  <RiskBadge
                    level={isReady ? (report.riskLevel ?? RiskLevel.UNASSESSED) : RiskLevel.PENDING}
                  />
                </td>
                <td>
                  <div className="flex items-center gap-4">
                    {canOpen ? (
                      <OLink
                        label={isDraft ? t('reports.continueDraft') : t('reports.viewReport')}
                        to={isDraft ? `/reports/create/${report._id}` : `/reports/${report._id}`}
                      >
                        <span
                          title={isDraft ? t('reports.continueDraft') : t('reports.viewReport')}
                        >
                          <FileTextIcon className="size-4" />
                        </span>
                      </OLink>
                    ) : (
                      <button
                        aria-label={t('reports.reportPending')}
                        className="opacity-40"
                        disabled
                        title={t('reports.availableWhenGenerationIsComplete')}
                        type="button"
                      >
                        <FileTextIcon className="size-4" />
                      </button>
                    )}
                    <RetryReportButton report={report} />
                  </div>
                </td>
              </tr>
            );
          })}
          {reports.length === 0 && (
            <tr>
              <td colSpan={6}>{t('reports.noReportsMatchTheseFilters')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const ReportHistory = ({ clientId }: { clientId?: string }) => {
  useLocale();

  const [params] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState(params.get('reportType') ?? 'ALL');
  const [status, setStatus] = useState(params.get('status') ?? ReportListStatus.COMPLETED);
  const [days, setDays] = useState(Number(params.get('days')) || 0);
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt'>(ReportSortBy.createdAt);
  const [order, setOrder] = useState<'asc' | 'desc'>(ReportSortOrder.desc);
  const [risk, setRisk] = useState('');
  const query = trpc.report.listReports.useQuery(
    {
      clientId,
      page,
      perPage: 10,
      search,
      reportType: type === ReportType.KYC || type === ReportType.KYB ? type : undefined,
      status: ReportListStatusSchema.safeParse(status).data ?? ReportListStatus.ALL,
      days: days || undefined,
      sortBy,
      sortOrder: order,
      riskLevel: AssessedRiskLevelSchema.safeParse(risk).data,
    },
    { refetchInterval: clientId ? false : 15000 },
  );
  const reset = () => setPage(1);
  return (
    <div>
      {!clientId && (
        <div aria-label={t('reports.reportStatus')} className="raven-tabs" role="tablist">
          {[
            {
              value: ReportListStatus.COMPLETED,
              get label() {
                return t('reports.completed');
              },
              icon: CheckCircleIcon,
            },
            {
              value: ReportStatus.PROCESSING,
              get label() {
                return t('reports.inProgress');
              },
              icon: LoaderCircleIcon,
            },
            {
              value: ReportStatus.DRAFT,
              get label() {
                return t('reports.savedDrafts');
              },
              icon: LayersIcon,
            },
          ].map((tab) => (
            <button
              aria-selected={status === tab.value}
              className={
                status === tab.value
                  ? 'flex items-center gap-2 text-indigo-600'
                  : 'flex items-center gap-2'
              }
              key={tab.value}
              onClick={() => {
                setStatus(tab.value);
                reset();
              }}
              role="tab"
              type="button"
            >
              <tab.icon className="size-5" />
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <ListToolbar
        onOrder={(value) => {
          setOrder(value);
          reset();
        }}
        onSearch={(value) => {
          setSearch(value);
          reset();
        }}
        onSortBy={(value) => {
          setSortBy(value as 'createdAt' | 'updatedAt');
          reset();
        }}
        order={order}
        search={search}
        sortBy={sortBy}
      >
        {!clientId && (
          <label className="flex flex-col gap-1 text-xs">
            {t('reports.type')}{' '}
            <select
              className="raven-select"
              onChange={(event) => {
                setType(event.target.value);
                reset();
              }}
              value={type}
            >
              <option value={ReportListStatus.ALL}>{t('reports.allTypes')}</option>
              <option>{t('reports.kyc')}</option>
              <option>{t('reports.kyb')}</option>
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs">
          {t('reports.period')}{' '}
          <select
            className="raven-select"
            onChange={(event) => {
              setDays(Number(event.target.value));
              reset();
            }}
            value={days}
          >
            <option value={0}>{t('reports.allTime')}</option>
            <option value={7}>{t('reports.thisWeek')}</option>
            <option value={30}>{t('reports.last30Days')}</option>
            <option value={180}>{t('reports.last6Months')}</option>
            <option value={365}>{t('reports.lastYear')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          {t('reports.status')}{' '}
          <select
            className="raven-select"
            onChange={(event) => {
              setStatus(event.target.value);
              reset();
            }}
            value={status}
          >
            <option value={ReportListStatus.ALL}>{t('workspace.allReports')}</option>
            <option value={ReportListStatus.DRAFT}>{t('reports.drafts')}</option>
            <option value={ReportListStatus.PROCESSING}>
              {t('reports.inProgress2')}
              {query.data?.processingCount ?? 0})
            </option>
            <option value={ReportListStatus.COMPLETED}>
              {t('reports.completed2')}
              {query.data?.completedCount ?? 0})
            </option>
            <option value={ReportListStatus.PENDING_REVIEW}>{t('reports.pendingReview2')}</option>
            <option value={ReportListStatus.INCOMPLETE}>{t('reports.failedPartial')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          {t('reports.riskFilter')}{' '}
          <select
            className="raven-select"
            onChange={(event) => {
              setRisk(event.target.value);
              reset();
            }}
            value={risk}
          >
            <option value="">{t('reports.allRisks')}</option>
            <option value={RiskLevel.HIGH}>{t('reports.high')}</option>
            <option value={RiskLevel.MEDIUM}>{t('reports.medium')}</option>
            <option value={RiskLevel.LOW}>{t('reports.low')}</option>
          </select>
        </label>
      </ListToolbar>
      <QueryState
        error={query.isError}
        loading={query.isLoading}
        retry={() => void query.refetch()}
      />
      {query.data && (
        <>
          <div className="mt-10 mb-4">
            <h2>{clientId ? t('reports.reportHistory') : t('reports.myReports')}</h2>
            <p className="raven-muted mt-2">
              {number(query.data.total)} {t('reports.reportsFound')}
            </p>
          </div>
          <ReportRows reports={query.data.data} />
          <div className="raven-table-footer flex flex-wrap items-center justify-between">
            <span className="raven-muted">
              {t('reports.showing')} {query.data.total ? (page - 1) * 10 + 1 : 0}–
              {Math.min(page * 10, query.data.total)} {t('reports.of')} {number(query.data.total)}{' '}
              {t('workspace.reports')}{' '}
            </span>
            <Pagination onChange={setPage} page={page} totalPages={query.data.totalPages} />
          </div>
        </>
      )}
    </div>
  );
};

const RetryReportButton = ({ report }: { report: Report }) => {
  useLocale();

  const role = useOrganizationStore((state) => state.currentOrganization.currentOrganizationRole);
  const utils = trpc.useUtils();
  const retry = trpc.report.retryReportGeneration.useMutation({
    onSuccess: () => {
      void utils.report.invalidate();
      toast.success(t('reports.reportGenerationRestarted'));
    },
    onError: (cause) => toast.error(errorMessage(AppError.parse(cause))),
  });
  if (
    role === UserRole.ORG_VIEWER ||
    ![ReportStatus.PARTIAL, ReportStatus.FAILED].some((status) => status === report.status) ||
    (report.decision?.outcome && report.decision.outcome !== ReportDecision.PENDING)
  ) {
    return null;
  }
  return (
    <button
      aria-label={t('reports.retryReportGeneration')}
      disabled={retry.isPending}
      onClick={() => retry.mutate({ reportId: report._id })}
      title={t('reports.retryReportGeneration')}
      type="button"
    >
      <RotateCcwIcon className="size-4" />
    </button>
  );
};
